import { useEffect, useRef } from 'react';

import { useClaim } from '~/components/ClaimContainer';
import useDataFetcher from '~/components/useDataFetcher';
import type { DocumentRequestListResponse } from '~/server_shared/openapi/data-contracts';

// The organization in the path is the claim's own, not the logged-in user's: a super-organization
// user reads another organization's claim.
export const claimDocumentRequestsUrl = (organizationId: number, claimId: number): string =>
  `/api/v1/organizations/${organizationId}/claims/${claimId}/document_requests`;

const fileUrl = (organizationId: number, claimId: number, documentRequestId: number, uploadedFileId: string) =>
  `${claimDocumentRequestsUrl(organizationId, claimId)}/${documentRequestId}/files/${uploadedFileId}`;

export const documentRequestFileDownloadUrl = (
  organizationId: number,
  claimId: number,
  documentRequestId: number,
  uploadedFileId: string
): string => `${fileUrl(organizationId, claimId, documentRequestId, uploadedFileId)}/download_url`;

export const documentRequestFileRescanUrl = (
  organizationId: number,
  claimId: number,
  documentRequestId: number,
  uploadedFileId: string
): string => `${fileUrl(organizationId, claimId, documentRequestId, uploadedFileId)}/rescan`;

export const documentRequestAttachUrl = (organizationId: number, claimId: number, documentRequestId: number): string =>
  `${claimDocumentRequestsUrl(organizationId, claimId)}/${documentRequestId}/attach`;

export const documentRequestCancelUrl = (organizationId: number, claimId: number, documentRequestId: number): string =>
  `${claimDocumentRequestsUrl(organizationId, claimId)}/${documentRequestId}/cancel`;

export const useClaimDocumentRequests = ({ shouldFetch = true }: { shouldFetch?: boolean } = {}) => {
  const { claim, reloadingUuid } = useClaim({ claimRequired: true });
  const lastReloadingUuidRef = useRef(reloadingUuid);

  const fetched = useDataFetcher<DocumentRequestListResponse>(
    claimDocumentRequestsUrl(claim.organization_id, claim.id),
    null,
    {
      shouldFetch,
      // Fetched on every Documents-tab visit; the section shows the failure in place of its tables.
      shouldOpenErrorDialog: false,
    }
  );
  const { isLoading, reloadData } = fetched;

  // A claim refresh reloads the list too, so a link generated from the claim's Actions menu shows here
  // without a page reload. Only while this list is mounted and shown: it is fetched afresh on every visit.
  useEffect(() => {
    if (!isLoading && shouldFetch && reloadingUuid !== lastReloadingUuidRef.current) {
      lastReloadingUuidRef.current = reloadingUuid;
      reloadData();
    }
  }, [isLoading, shouldFetch, reloadingUuid, reloadData]);

  return fetched;
};
